import React, { useCallback, useEffect, useRef, useState } from 'react';
import useHorizontalScrollSync from '@components/StickyHeader/hooks/useHorizontalScrollSync.hook';
import useStickyHeader from '@components/StickyHeader/hooks/useStickyHeader.hook';
import styles from './StickyHeader.module.scss';

interface StickyHeaderProps {
  topComponentClass?: string;
  containerClass?: string;
  overflow?: boolean;
  children: React.ReactNode;
}

const StickyHeader: React.FC<StickyHeaderProps> = ({ topComponentClass, containerClass, overflow, children }) => {
  const stickyHeaderRef = useRef<HTMLDivElement>(null);

  // Dimensions et position du header sticky
  const [stickyHeaderBounding, setStickyHeaderBounding] = useState<DOMRect>();

  // Dimensions et position du conteneur
  const [containerElementBounding, setContainerElementBounding] = useState<DOMRect>();

  // La position inférieure de l'élément de référence en haut
  const [topElementBounding, setTopElementBounding] = useState<DOMRect>();

  const [stickyHeaderElementHTML, setStickyHeaderElementHTML] = useState<HTMLElement | null>();
  const [topElementHTML, setTopElementHTML] = useState<HTMLElement | null>();
  const [containerElementHTML, setContainerElementHTML] = useState<HTMLElement | null>();

  // Mise à jour des references HTML des composants
  useEffect(() => {
    const stickyHeaderElement = stickyHeaderRef.current;
    const topElement = topComponentClass ? (document.querySelector(`${topComponentClass}`) as HTMLElement) : null;
    const containerElement = containerClass ? (document.querySelector(`${containerClass}`) as HTMLElement) : null;

    setStickyHeaderElementHTML(stickyHeaderElement);
    setTopElementHTML(topElement);
    setContainerElementHTML(containerElement);
  }, [stickyHeaderRef, topComponentClass, containerClass]);

  // Mise à jour les valeurs des positions des éléments
  const updateBounds = useCallback(() => {
    if (stickyHeaderElementHTML && topElementHTML && containerElementHTML) {
      setTopElementBounding(topElementHTML.getBoundingClientRect());
      setStickyHeaderBounding(stickyHeaderElementHTML.getBoundingClientRect());
      setContainerElementBounding(containerElementHTML.getBoundingClientRect());
    }
  }, [topElementHTML, stickyHeaderElementHTML, containerElementHTML]);

  // Gestion de l’événement de défilement vertical pour mettre à jour les positions
  useEffect(() => {
    window.addEventListener('scroll', updateBounds);
    return () => {
      window.removeEventListener('scroll', updateBounds);
    };
  }, [updateBounds]);

  // Gestion de la synchronisation du scroll horizontal
  useHorizontalScrollSync(containerElementHTML, stickyHeaderElementHTML, overflow);

  // Gestion des changements d’état du header sticky
  const { isSticky, stickyTranslationY } = useStickyHeader(
    stickyHeaderBounding,
    topElementBounding,
    containerElementBounding
  );

  return (
    <div
      className={`${styles.stickyOuterWrapper} ${isSticky ? styles.active : ''}`}
      style={isSticky && stickyHeaderBounding ? { height: `${stickyHeaderBounding.height}px` } : {}}
    >
      <div
        ref={stickyHeaderRef}
        className={`${styles.stickyInnerWrapper} ${overflow ? styles.overflow : ''}`}
        style={{
          position: isSticky ? 'fixed' : 'relative',
          transform: `translate3d(0px, ${Math.round(stickyTranslationY)}px, 0px)`,
          width: `${containerElementBounding?.width}px`,
          zIndex: 2,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default StickyHeader;
